import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './BinContentsPage.css';

function BinContentsPage() {
  const { binCode } = useParams();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    sku: '',
    adjustment_type: 'add',
    quantity: '',
    reason: ''
  });
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    fetchBinInventory();
  }, [binCode]);

  const fetchBinInventory = async () => {
    setLoading(true);
    setError(null);
    try {
      // Query NetSuite directly by bin name
      const response = await axios.get(`/api/bins/${encodeURIComponent(binCode)}/inventory`);
      setInventory(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch bin inventory:', err);
      setError(err.response?.data?.error || 'Failed to load bin inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustment = async (e) => {
    e.preventDefault();
    setAdjusting(true);
    try {
      await axios.post(`/api/bins/${encodeURIComponent(binCode)}/adjust`, {
        ...adjustmentData,
        quantity: parseInt(adjustmentData.quantity)
      });
      alert('Inventory adjustment recorded successfully');
      setShowAdjustment(false);
      setAdjustmentData({
        sku: '',
        adjustment_type: 'add',
        quantity: '',
        reason: ''
      });
      fetchBinInventory(); // Refresh inventory
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to record adjustment');
    } finally {
      setAdjusting(false);
    }
  };

  if (loading) {
    return (
      <div className="bin-contents-page">
        <LoadingSpinner />
      </div>
    );
  }

  const totalItems = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div className="bin-contents-page">
      <header className="bin-header">
        <div className="bin-header-top">
          <Link to="/bin-labels" className="back-btn">Back</Link>
        </div>
        <h1 className="bin-code-title">{binCode}</h1>
      </header>

      <div className="bin-stats">
        <div className="stat-card">
          <div className="stat-value">{inventory.length}</div>
          <div className="stat-label">SKUs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalItems}</div>
          <div className="stat-label">Total Units</div>
        </div>
      </div>

      {error && (
        <div className="bin-error-banner">
          {error}
        </div>
      )}

      <div className="bin-inventory">
        <div className="section-header">
          <h2>Contents</h2>
          <button
            className="adjust-btn"
            onClick={() => setShowAdjustment(!showAdjustment)}
          >
            {showAdjustment ? 'Cancel' : 'Adjust Inventory'}
          </button>
        </div>

        {showAdjustment && (
          <form onSubmit={handleAdjustment} className="adjustment-form">
            <div className="form-row">
              <label>SKU *</label>
              <input
                type="text"
                value={adjustmentData.sku}
                onChange={(e) => setAdjustmentData({...adjustmentData, sku: e.target.value.toUpperCase()})}
                placeholder="Enter SKU"
                required
              />
            </div>
            <div className="form-row-group">
              <div className="form-row">
                <label>Type *</label>
                <select
                  value={adjustmentData.adjustment_type}
                  onChange={(e) => setAdjustmentData({...adjustmentData, adjustment_type: e.target.value})}
                >
                  <option value="add">Add</option>
                  <option value="remove">Remove</option>
                  <option value="count">Count (Set To)</option>
                </select>
              </div>
              <div className="form-row">
                <label>Quantity *</label>
                <input
                  type="number"
                  value={adjustmentData.quantity}
                  onChange={(e) => setAdjustmentData({...adjustmentData, quantity: e.target.value})}
                  min="1"
                  placeholder="Qty"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <label>Reason</label>
              <input
                type="text"
                value={adjustmentData.reason}
                onChange={(e) => setAdjustmentData({...adjustmentData, reason: e.target.value})}
                placeholder="Optional reason for adjustment"
              />
            </div>
            <button type="submit" disabled={adjusting} className="submit-btn">
              {adjusting ? 'Processing...' : 'Submit Adjustment'}
            </button>
          </form>
        )}

        {inventory.length === 0 ? (
          <div className="empty-bin">
            <p>No inventory found in this bin</p>
            <small>This bin may be empty or the bin code may not exist in NetSuite</small>
          </div>
        ) : (
          <div className="inventory-list">
            {inventory.map((item, index) => (
              <div key={index} className="inventory-item">
                <div className="item-main">
                  <Link to={`/ops/${item.sku}`} className="item-sku">{item.sku}</Link>
                  <div className="item-qty">{item.quantity}</div>
                </div>
                {item.description && (
                  <div className="item-description">{item.description}</div>
                )}
                {item.lot_number && (
                  <div className="item-lot">Lot: {item.lot_number}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BinContentsPage;
